import type { BaseFilter } from "@circle-react-shared/MemberFilters/types";
import type { FieldTypes } from "@circle-react-shared/ProfileFields/constants";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import flatten from "lodash/flatten";
import { useMemo } from "react";
import { keepPreviousData, useInfiniteQuery } from "react-query";

import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";

export const COMMUNITY_MEMBERS_QUERY_KEY = "community-members";

export const SORT_OPTIONS_MAP = {
  ALPHABETICAL: "alphabetical",
  OLDEST: "oldest",
  LATEST: "latest",
  ROLE: "role",
};

export const getPageSize = () => {
  // 4K screens (typically 2160px height or more). Capped at 100 to match the
  // ClickHouse-backed V3 contacts search max per_page.
  if (window.innerHeight >= 2160) return 100;
  // 2K/QHD screens (typically around 1440px height)
  if (window.innerHeight >= 1440) return 70;
  // Full HD screens (typically around 1080px height)
  if (window.innerHeight >= 1080) return 50;
  // Medium-sized screens
  if (window.innerHeight >= 925) return 30;
  // Smaller screens (laptops, tablets)
  return 15;
};

export interface CommunityMembersFilter extends Pick<
  BaseFilter,
  "key" | "value"
> {
  filter_type?: string;
  id?: string;
  profile_field_id?: number;
  profile_field_type?: FieldTypes;
}

interface UseCommunityMembersInfiniteQueryArgs {
  filters?: CommunityMembersFilter[];
  initialPage?: number;
  isAdminScope?: boolean;
  // When true, routes to the V3 (ClickHouse-backed) contacts search endpoint
  // instead of the V2 (Elasticsearch-backed) community members endpoint.
  // Defaults to false so existing callers are unaffected. Callers should
  // forward the rollout-specific community setting that controls their
  // surface.
  isClickHouseSearchEnabled?: boolean;
  options?: Record<string, any>;
  order?: Record<string, "asc" | "desc">;
  perPage?: number;
  scope?: {
    status: string;
    visible_in_member_directory?: boolean;
    exclude_empty_name?: boolean;
  };
  sort?: string;
}

export const useCommunityMembersInfiniteQuery = (
  args: UseCommunityMembersInfiniteQueryArgs = {},
) => {
  const {
    sort = SORT_OPTIONS_MAP.ALPHABETICAL,
    order,
    filters = [],
    perPage = getPageSize(),
    initialPage = 1,
    options = {},
    scope = {},
    isAdminScope = false,
    isClickHouseSearchEnabled = false,
  } = args;

  const fetchPage = ({ pageParam = initialPage }) => {
    let page = 1;

    if (typeof pageParam === "number" && pageParam > 0) {
      page = pageParam;
    }

    const params: Record<string, unknown> = {
      page,
      sort,
      filters,
      per_page: perPage,
      ...scope,
    };

    if (order) {
      params.order = order;
    }

    const route = isClickHouseSearchEnabled
      ? isAdminScope
        ? internalApi.admin.search.contacts.indexV3
        : internalApi.search.contacts.indexV3
      : isAdminScope
        ? internalApi.admin.search.communityMembers.indexV2
        : internalApi.search.communityMembers.indexV2;

    return reactQueryGet(route({ params }));
  };

  const queryKey: any[] = [
    COMMUNITY_MEMBERS_QUERY_KEY,
    sort,
    order,
    filters,
    perPage,
    isClickHouseSearchEnabled,
  ];

  const queryOptions = {
    getNextPageParam: (lastPage: any) =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
    getPreviousPageParam: (firstPage: any) =>
      firstPage.page > 1 ? firstPage.page - 1 : undefined,
    placeholderData: keepPreviousData,
    ...options,
  };

  const result = useInfiniteQuery({
    initialPageParam: initialPage,
    queryKey,
    queryFn: fetchPage,
    ...queryOptions,
  });
  const pages = useMemo(() => result.data?.pages || [], [result.data?.pages]);
  const totalMembers: number = pages[0]?.meta?.total_count || 0;
  const totalQueryMembers: number = pages[0]?.count || 0;
  const members = useMemo(
    () => flatten(pages.map(page => page.records)),
    [pages],
  );
  return {
    data: result.data,
    isLoading: result.isInitialLoading,
    isFetching: result.isFetching,
    isFetchingNextPage: result.isFetchingNextPage,
    isFetchingPreviousPage: result.isFetchingPreviousPage,
    hasNextPage: result.hasNextPage,
    hasPreviousPage: result.hasPreviousPage,
    fetchNextPage: result.fetchNextPage,
    fetchPreviousPage: result.fetchPreviousPage,
    refetch: result.refetch,
    isError: result.isError,
    isFetched: result.isFetched,
    queryKey,
    totalMembers,
    totalQueryMembers,
    members,
    pages,
  };
};
